import type { RuleDefinition } from '../types/index.js';

export const rules: RuleDefinition[] = [
  // ============================================
  // SECRETS
  // ============================================
  {
    id: 'secrets.no-plaintext',
    name: 'No Plaintext Secrets',
    description: 'Detect hardcoded secrets, API keys, passwords in source code',
    category: 'secrets',
    severity: 'error',
    autofix: false,
    blocking: true,
    scope: 'all',
    tool: 'gitleaks',
    docs: 'https://vibeguard.dev/rules/secrets-no-plaintext',
    rationale:
      'Hardcoded secrets in source code are a critical security vulnerability. They can be exposed through version control, logs, or error messages.',
    fix: 'Move secrets to environment variables or a secrets manager. Use .env files for local development (never commit them).',
    examples: {
      bad: `const API_KEY = "sk-1234567890abcdef";`,
      good: `const API_KEY = process.env.API_KEY;`,
    },
    levels: { basic: true, standard: true, strict: true },
  },
  {
    id: 'secrets.no-env-commit',
    name: 'No .env Files in Git',
    description: 'Ensure .env files are not committed to version control',
    category: 'secrets',
    severity: 'error',
    autofix: true,
    blocking: true,
    scope: 'all',
    tool: 'builtin',
    docs: 'https://vibeguard.dev/rules/secrets-no-env-commit',
    rationale:
      '.env files often contain sensitive configuration. They should never be committed.',
    fix: 'Add .env to .gitignore. Remove any committed .env files from git history.',
    levels: { basic: true, standard: true, strict: true },
  },
  {
    id: 'secrets.gitignore-required',
    name: 'Gitignore Required',
    description: 'Ensure .gitignore exists with common sensitive patterns',
    category: 'secrets',
    severity: 'warn',
    autofix: true,
    blocking: false,
    scope: 'all',
    tool: 'builtin',
    docs: 'https://vibeguard.dev/rules/secrets-gitignore-required',
    rationale: 'A proper .gitignore prevents accidental commits of sensitive files.',
    fix: 'Create or update .gitignore with patterns for .env, node_modules, etc.',
    levels: { basic: true, standard: true, strict: true },
  },

  // ============================================
  // DEPENDENCIES
  // ============================================
  {
    id: 'deps.lockfile.required',
    name: 'Lockfile Required',
    description: 'Ensure package-lock.json, yarn.lock, or pnpm-lock.yaml exists',
    category: 'deps',
    severity: 'error',
    autofix: true,
    blocking: true,
    scope: 'all',
    tool: 'builtin',
    docs: 'https://vibeguard.dev/rules/deps-lockfile-required',
    rationale:
      'Lockfiles ensure reproducible builds and prevent supply chain attacks through version drift.',
    fix: 'Run npm install, yarn install, or pnpm install to generate a lockfile.',
    levels: { basic: true, standard: true, strict: true },
  },
  {
    id: 'deps.no-unpinned',
    name: 'No Unpinned Dependencies',
    description: 'Warn about dependencies using ranges like ^, ~, or *',
    category: 'deps',
    severity: 'warn',
    autofix: false,
    blocking: false,
    scope: 'all',
    tool: 'builtin',
    docs: 'https://vibeguard.dev/rules/deps-no-unpinned',
    rationale:
      'Unpinned dependencies can introduce breaking changes or vulnerabilities unexpectedly.',
    fix: 'Pin dependencies to exact versions or use a lockfile.',
    levels: { basic: false, standard: true, strict: true },
  },
  {
    id: 'deps.no-git-deps',
    name: 'No Git Dependencies',
    description: 'Disallow dependencies from git URLs',
    category: 'deps',
    severity: 'warn',
    autofix: false,
    blocking: false,
    scope: 'all',
    tool: 'builtin',
    docs: 'https://vibeguard.dev/rules/deps-no-git-deps',
    rationale:
      'Git dependencies bypass npm registry security checks and can change without version bumps.',
    fix: 'Publish the dependency to npm or use a private registry.',
    levels: { basic: false, standard: true, strict: true },
  },
  {
    id: 'deps.no-vulnerable',
    name: 'No Vulnerable Dependencies',
    description: 'Scan dependencies for known vulnerabilities',
    category: 'deps',
    severity: 'error',
    autofix: false,
    blocking: true,
    scope: 'all',
    tool: 'osv-scanner',
    docs: 'https://vibeguard.dev/rules/deps-no-vulnerable',
    rationale: 'Known vulnerabilities in dependencies are a common attack vector.',
    fix: 'Update vulnerable dependencies or find alternatives.',
    levels: { basic: true, standard: true, strict: true },
  },
  {
    id: 'deps.no-typosquatting',
    name: 'No Typosquatting Packages',
    description: 'Detect potentially malicious packages with names similar to popular ones',
    category: 'deps',
    severity: 'error',
    autofix: false,
    blocking: true,
    scope: 'all',
    tool: 'builtin',
    docs: 'https://vibeguard.dev/rules/deps-no-typosquatting',
    rationale:
      'Typosquatting is a supply chain attack where malicious packages mimic popular package names.',
    fix: 'Verify package names carefully. Use official package names.',
    levels: { basic: true, standard: true, strict: true },
  },
  {
    id: 'deps.license-check',
    name: 'License Compatibility Check',
    description: 'Ensure dependencies have compatible licenses',
    category: 'deps',
    severity: 'warn',
    autofix: false,
    blocking: false,
    scope: 'all',
    tool: 'builtin',
    docs: 'https://vibeguard.dev/rules/deps-license-check',
    rationale: 'Incompatible licenses can create legal issues for your project.',
    fix: 'Review and replace dependencies with incompatible licenses.',
    levels: { basic: false, standard: false, strict: true },
  },

  // ============================================
  // SECURITY
  // ============================================
  {
    id: 'security.headers.required',
    name: 'Security Headers Required',
    description: 'Ensure security headers are configured (CSP, HSTS, etc.)',
    category: 'security',
    severity: 'warn',
    autofix: false,
    blocking: false,
    scope: 'all',
    tool: 'semgrep',
    docs: 'https://vibeguard.dev/rules/security-headers-required',
    rationale:
      'Security headers protect against XSS, clickjacking, and other browser-based attacks.',
    fix: 'Add helmet middleware (Express) or configure headers in next.config.js.',
    examples: {
      good: `import helmet from 'helmet';\napp.use(helmet());`,
    },
    levels: { basic: false, standard: true, strict: true },
  },
  {
    id: 'security.cors.safe-default',
    name: 'Safe CORS Configuration',
    description: 'Detect overly permissive CORS configurations',
    category: 'security',
    severity: 'error',
    autofix: false,
    blocking: true,
    scope: 'all',
    tool: 'semgrep',
    docs: 'https://vibeguard.dev/rules/security-cors-safe-default',
    rationale: 'Permissive CORS (origin: *) can expose your API to cross-origin attacks.',
    fix: 'Restrict CORS to specific trusted origins.',
    examples: {
      bad: `cors({ origin: '*' })`,
      good: `cors({ origin: ['https://myapp.com'] })`,
    },
    levels: { basic: true, standard: true, strict: true },
  },
  {
    id: 'security.no-eval',
    name: 'No eval() or Function()',
    description: 'Disallow eval, Function constructor, and similar dynamic code execution',
    category: 'security',
    severity: 'error',
    autofix: false,
    blocking: true,
    scope: 'all',
    tool: 'eslint',
    docs: 'https://vibeguard.dev/rules/security-no-eval',
    rationale: 'Dynamic code execution is a major injection vulnerability vector.',
    fix: 'Use safer alternatives like JSON.parse for data, or refactor logic.',
    levels: { basic: true, standard: true, strict: true },
  },
  {
    id: 'security.no-unsafe-regex',
    name: 'No Unsafe Regular Expressions',
    description: 'Detect regex patterns vulnerable to ReDoS attacks',
    category: 'security',
    severity: 'warn',
    autofix: false,
    blocking: false,
    scope: 'all',
    tool: 'eslint',
    docs: 'https://vibeguard.dev/rules/security-no-unsafe-regex',
    rationale: 'Catastrophic backtracking in regex can cause denial of service.',
    fix: 'Simplify regex patterns or use a safe regex library.',
    levels: { basic: false, standard: true, strict: true },
  },
  {
    id: 'security.no-prototype-pollution',
    name: 'No Prototype Pollution',
    description: 'Detect patterns that could lead to prototype pollution',
    category: 'security',
    severity: 'error',
    autofix: false,
    blocking: true,
    scope: 'all',
    tool: 'semgrep',
    docs: 'https://vibeguard.dev/rules/security-no-prototype-pollution',
    rationale: 'Prototype pollution can lead to property injection and RCE.',
    fix: 'Use Object.create(null) for dictionaries, validate object keys.',
    levels: { basic: true, standard: true, strict: true },
  },

  // ============================================
  // AUTH
  // ============================================
  {
    id: 'auth.no-weak-jwt',
    name: 'No Weak JWT Configuration',
    description: 'Detect weak JWT algorithms (none, HS256 with weak secrets)',
    category: 'auth',
    severity: 'error',
    autofix: false,
    blocking: true,
    scope: 'all',
    tool: 'semgrep',
    docs: 'https://vibeguard.dev/rules/auth-no-weak-jwt',
    rationale: 'Weak JWT configuration allows token forgery and authentication bypass.',
    fix: 'Use RS256 or ES256 algorithms. Never use "none". Use strong secrets.',
    examples: {
      bad: `jwt.sign(payload, 'secret', { algorithm: 'none' })`,
      good: `jwt.sign(payload, privateKey, { algorithm: 'RS256' })`,
    },
    levels: { basic: true, standard: true, strict: true },
  },
  {
    id: 'auth.no-hardcoded-credentials',
    name: 'No Hardcoded Credentials',
    description: 'Detect hardcoded usernames, passwords, or tokens in auth logic',
    category: 'auth',
    severity: 'error',
    autofix: false,
    blocking: true,
    scope: 'all',
    tool: 'semgrep',
    docs: 'https://vibeguard.dev/rules/auth-no-hardcoded-credentials',
    rationale: 'Hardcoded credentials are easily discovered and exploited.',
    fix: 'Use environment variables or a secrets manager.',
    levels: { basic: true, standard: true, strict: true },
  },
  {
    id: 'auth.session-secure',
    name: 'Secure Session Configuration',
    description: 'Ensure session cookies have secure, httpOnly, sameSite flags',
    category: 'auth',
    severity: 'warn',
    autofix: false,
    blocking: false,
    scope: 'all',
    tool: 'semgrep',
    docs: 'https://vibeguard.dev/rules/auth-session-secure',
    rationale: 'Insecure session cookies can be stolen via XSS or MITM attacks.',
    fix: 'Set secure: true, httpOnly: true, sameSite: "strict" on session cookies.',
    levels: { basic: false, standard: true, strict: true },
  },

  // ============================================
  // API
  // ============================================
  {
    id: 'api.validation.required',
    name: 'Input Validation Required',
    description: 'Ensure API endpoints validate input with a schema library',
    category: 'api',
    severity: 'warn',
    autofix: false,
    blocking: false,
    scope: 'all',
    tool: 'semgrep',
    docs: 'https://vibeguard.dev/rules/api-validation-required',
    rationale: 'Unvalidated input is the root cause of most injection vulnerabilities.',
    fix: 'Use zod, joi, yup, or similar for input validation.',
    examples: {
      bad: `app.post('/user', (req, res) => { db.insert(req.body); });`,
      good: `app.post('/user', (req, res) => { const data = userSchema.parse(req.body); });`,
    },
    levels: { basic: false, standard: true, strict: true },
  },
  {
    id: 'api.rate-limiting',
    name: 'Rate Limiting Required',
    description: 'Ensure rate limiting is configured on API endpoints',
    category: 'api',
    severity: 'warn',
    autofix: false,
    blocking: false,
    scope: 'all',
    tool: 'semgrep',
    docs: 'https://vibeguard.dev/rules/api-rate-limiting',
    rationale: 'Without rate limiting, APIs are vulnerable to brute force and DoS attacks.',
    fix: 'Add express-rate-limit or similar middleware.',
    levels: { basic: false, standard: false, strict: true },
  },
  {
    id: 'api.no-sensitive-logging',
    name: 'No Sensitive Data in Logs',
    description: 'Detect logging of passwords, tokens, or PII',
    category: 'api',
    severity: 'error',
    autofix: false,
    blocking: true,
    scope: 'all',
    tool: 'semgrep',
    docs: 'https://vibeguard.dev/rules/api-no-sensitive-logging',
    rationale: 'Sensitive data in logs can be exposed through log aggregation systems.',
    fix: 'Redact sensitive fields before logging. Use structured logging.',
    levels: { basic: true, standard: true, strict: true },
  },

  // ============================================
  // SQL
  // ============================================
  {
    id: 'sql.no-string-concat',
    name: 'No SQL String Concatenation',
    description: 'Detect SQL queries built with string concatenation',
    category: 'sql',
    severity: 'error',
    autofix: false,
    blocking: true,
    scope: 'all',
    tool: 'semgrep',
    docs: 'https://vibeguard.dev/rules/sql-no-string-concat',
    rationale: 'String concatenation in SQL queries leads to SQL injection.',
    fix: 'Use parameterized queries or an ORM.',
    examples: {
      bad: `db.query("SELECT * FROM users WHERE id = " + userId)`,
      good: `db.query("SELECT * FROM users WHERE id = $1", [userId])`,
    },
    levels: { basic: true, standard: true, strict: true },
  },
  {
    id: 'sql.no-raw-queries',
    name: 'Prefer ORM Over Raw Queries',
    description: 'Warn when using raw SQL queries instead of ORM methods',
    category: 'sql',
    severity: 'info',
    autofix: false,
    blocking: false,
    scope: 'all',
    tool: 'semgrep',
    docs: 'https://vibeguard.dev/rules/sql-no-raw-queries',
    rationale: 'ORMs provide built-in protection against SQL injection.',
    fix: 'Use Prisma, Drizzle, or TypeORM query builders.',
    levels: { basic: false, standard: false, strict: true },
  },

  // ============================================
  // TESTS
  // ============================================
  {
    id: 'tests.unit.required',
    name: 'Unit Tests Required',
    description: 'Ensure test files exist for the project',
    category: 'tests',
    severity: 'warn',
    autofix: false,
    blocking: false,
    scope: 'all',
    tool: 'builtin',
    docs: 'https://vibeguard.dev/rules/tests-unit-required',
    rationale: 'Tests catch regressions and document expected behavior.',
    fix: 'Create test files using Jest, Vitest, or your preferred test runner.',
    levels: { basic: false, standard: true, strict: true },
  },
  {
    id: 'tests.coverage.min-50',
    name: 'Minimum 50% Test Coverage',
    description: 'Ensure at least 50% code coverage',
    category: 'tests',
    severity: 'warn',
    autofix: false,
    blocking: false,
    scope: 'all',
    tool: 'vitest',
    docs: 'https://vibeguard.dev/rules/tests-coverage-min-50',
    rationale: 'Minimum coverage ensures critical paths are tested.',
    fix: 'Add tests for uncovered code paths.',
    levels: { basic: false, standard: true, strict: false },
  },
  {
    id: 'tests.coverage.min-80',
    name: 'Minimum 80% Test Coverage',
    description: 'Ensure at least 80% code coverage',
    category: 'tests',
    severity: 'error',
    autofix: false,
    blocking: true,
    scope: 'all',
    tool: 'vitest',
    docs: 'https://vibeguard.dev/rules/tests-coverage-min-80',
    rationale: 'High coverage reduces the risk of regressions.',
    fix: 'Add tests for uncovered code paths.',
    levels: { basic: false, standard: false, strict: true },
  },
  {
    id: 'tests.no-skipped',
    name: 'No Skipped Tests',
    description: 'Detect .skip() or .only() left in test files',
    category: 'tests',
    severity: 'warn',
    autofix: false,
    blocking: false,
    scope: 'all',
    tool: 'eslint',
    docs: 'https://vibeguard.dev/rules/tests-no-skipped',
    rationale: 'Skipped tests can hide failures. .only() can exclude other tests.',
    fix: 'Remove .skip() and .only() before committing.',
    levels: { basic: true, standard: true, strict: true },
  },

  // ============================================
  // LOGGING
  // ============================================
  {
    id: 'logging.no-pii',
    name: 'No PII in Logs',
    description: 'Detect logging of personally identifiable information',
    category: 'logging',
    severity: 'error',
    autofix: false,
    blocking: true,
    scope: 'all',
    tool: 'semgrep',
    docs: 'https://vibeguard.dev/rules/logging-no-pii',
    rationale: 'PII in logs violates privacy regulations (GDPR, CCPA).',
    fix: 'Redact or hash PII before logging.',
    levels: { basic: false, standard: true, strict: true },
  },
  {
    id: 'logging.no-console',
    name: 'No console.log in Production',
    description: 'Detect console.log statements that should use a logger',
    category: 'logging',
    severity: 'warn',
    autofix: false,
    blocking: false,
    scope: 'all',
    tool: 'eslint',
    docs: 'https://vibeguard.dev/rules/logging-no-console',
    rationale: 'console.log lacks log levels, formatting, and can leak sensitive data.',
    fix: 'Use a structured logger like pino or winston.',
    levels: { basic: false, standard: true, strict: true },
  },

  // ============================================
  // CODE QUALITY
  // ============================================
  {
    id: 'code.no-any',
    name: 'No TypeScript any',
    description: 'Disallow the any type in TypeScript',
    category: 'code',
    severity: 'warn',
    autofix: false,
    blocking: false,
    scope: 'all',
    tool: 'eslint',
    docs: 'https://vibeguard.dev/rules/code-no-any',
    rationale: 'any disables type checking and can hide bugs.',
    fix: 'Use proper types, unknown, or generics instead.',
    levels: { basic: false, standard: true, strict: true },
  },
  {
    id: 'code.strict-mode',
    name: 'TypeScript Strict Mode',
    description: 'Ensure TypeScript strict mode is enabled',
    category: 'code',
    severity: 'warn',
    autofix: true,
    blocking: false,
    scope: 'all',
    tool: 'builtin',
    docs: 'https://vibeguard.dev/rules/code-strict-mode',
    rationale: 'Strict mode catches more errors at compile time.',
    fix: 'Add "strict": true to tsconfig.json.',
    levels: { basic: false, standard: true, strict: true },
  },
  {
    id: 'code.no-unused-vars',
    name: 'No Unused Variables',
    description: 'Detect unused variables and imports',
    category: 'code',
    severity: 'warn',
    autofix: true,
    blocking: false,
    scope: 'all',
    tool: 'eslint',
    docs: 'https://vibeguard.dev/rules/code-no-unused-vars',
    rationale: 'Unused code is dead weight and can indicate bugs.',
    fix: 'Remove unused variables or prefix with underscore.',
    levels: { basic: true, standard: true, strict: true },
  },

  // ============================================
  // CONFIG
  // ============================================
  {
    id: 'config.node-version',
    name: 'Node Version Specified',
    description: 'Ensure Node.js version is specified in package.json or .nvmrc',
    category: 'config',
    severity: 'info',
    autofix: true,
    blocking: false,
    scope: 'all',
    tool: 'builtin',
    docs: 'https://vibeguard.dev/rules/config-node-version',
    rationale: 'Specifying Node version ensures consistent environments.',
    fix: 'Add engines.node to package.json or create .nvmrc.',
    levels: { basic: false, standard: true, strict: true },
  },
  {
    id: 'config.editor-config',
    name: 'EditorConfig Present',
    description: 'Ensure .editorconfig exists for consistent formatting',
    category: 'config',
    severity: 'info',
    autofix: true,
    blocking: false,
    scope: 'all',
    tool: 'builtin',
    docs: 'https://vibeguard.dev/rules/config-editor-config',
    rationale: 'EditorConfig ensures consistent formatting across editors.',
    fix: 'Create .editorconfig with project standards.',
    levels: { basic: false, standard: true, strict: true },
  },
];

export const ruleMap = new Map<string, RuleDefinition>(rules.map((r) => [r.id, r]));

export function getRuleById(id: string): RuleDefinition | undefined {
  return ruleMap.get(id);
}

export function getRulesByCategory(category: string): RuleDefinition[] {
  return rules.filter((r) => r.category === category);
}

export function getRulesByLevel(level: 'basic' | 'standard' | 'strict'): RuleDefinition[] {
  return rules.filter((r) => r.levels[level]);
}

export function getRulesByTool(tool: string): RuleDefinition[] {
  return rules.filter((r) => r.tool === tool);
}
